import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import LegalDocument, { type LegalDocumentId } from '@/components/LegalDocument'

/**
 * The page behind the footer's Terms, Privacy and Cookies links.
 *
 * Those links pointed at routes that did not exist, so every one of them fell
 * through to the 404 page: the terms could not be read at all, which matters
 * most in an on-premise install where there is no public site to fall back on.
 * The text ships with the console for the same reason, rather than living
 * behind a link to somewhere on the internet.
 *
 * Deliberately outside ProtectedRoute: someone who has just declined the
 * agreement, and is therefore signed out, still has to be able to read what
 * they declined.
 */

interface LegalPageProps {
  document: LegalDocumentId
}

export default function LegalPage({ document }: LegalPageProps) {
  const { t } = useTranslation('legal')

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors mb-6"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('backToHome')}
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <header className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900">{t(`${document}.title`)}</h1>
            <p className="text-gray-600 mt-2">{t(`${document}.subtitle`)}</p>
          </header>

          <LegalDocument document={document} showTitle={false} />
        </div>
      </div>
    </div>
  )
}
